/*
 * New PDP buy stack (sections/new-pdp-main.liquid).
 *
 * <new-pdp-variant-radios> subclasses VariantRadios (global.js); the base
 * variant-change flow is inherited unchanged. Because the tag name differs,
 * global.js's updateVariantPickerFromReturnHTML() no-ops — the picker DOM is
 * never replaced, so option states are owned by refreshOptionStates() below.
 *
 * Option order varies by product (Wide Mouth: Size then Color; others the
 * reverse) — size behavior is keyed on the option's name, never its position:
 * every size stays selectable; one the current color lacks is struck-through
 * (.is-unavailable, same look as a sold-out swatch) and picking it keeps the
 * size and snaps the color to the first available (in-stock preferred)
 * variant. Colors missing for the selected size stay hidden + disabled.
 * Sold-out combos stay selectable (.is-soldout); values with no live variant
 * at all get .is-unavailable + disabled; draft variants
 * (#new-pdp-draft-variants) are treated as nonexistent.
 */
(() => {
  function register() {
    const Base = customElements.get('variant-radios');
    if (!Base || customElements.get('new-pdp-variant-radios')) return;

    customElements.define(
      'new-pdp-variant-radios',
      class NewPdpVariantRadios extends Base {
        constructor() {
          super();

          this.draftIds = new Set();
          try {
            JSON.parse(
              this.querySelector('#new-pdp-draft-variants').textContent
            ).forEach((id) => this.draftIds.add(id));
          } catch (e) {
            // no draft data — treat all variants as live
          }

          this.refreshOptionStates();
        }

        liveVariants() {
          return this.variantData.filter((variant) => !this.draftIds.has(variant.id));
        }

        async onVariantChange(event) {
          // Must snapshot before reconcileSelection to isolate what it snapped
          const beforeSnap = this.getOptionsFromSelects();
          this.reconcileSelection(event);
          await super.onVariantChange(event);
          this.refreshOptionStates();
          this.announceVariantState(beforeSnap);
        }

        /*
         * Announces the two otherwise-silent outcomes of a variant change:
         * an auto-snapped color, and a sold-out/nonexistent selection.
         */
        announceVariantState(beforeSnap) {
          const status = this.querySelector('[id^="NewPdpVariantStatus"]');
          if (!status) return;

          const selected = this.getOptionsFromSelects();
          const containers = Array.from(this.querySelectorAll('.fieldset-container'));
          const parts = [];

          selected.forEach((value, index) => {
            if (beforeSnap[index] === value) return;
            const line = containers[index]
              ? containers[index].querySelector('.option-container__value')
              : null;
            parts.push(line ? line.textContent.replace(/\s+/g, ' ').trim() : value);
          });

          const live = this.liveVariants();
          const current = live.find((variant) =>
            variant.options.every((value, index) => value === selected[index])
          );
          if (!current) {
            if (this.dataset.textUnavailable) parts.push(this.dataset.textUnavailable);
          } else if (!current.available) {
            if (this.dataset.textSoldOut) {
              parts.push(`${selected.join(' ')} — ${this.dataset.textSoldOut}`);
            }
          }

          const message = parts.join('. ');
          if (!message) {
            status.textContent = '';
            return;
          }
          // Clear first so repeating the same message still announces
          status.textContent = '';
          setTimeout(() => {
            status.textContent = message;
          }, 100);
        }

        // Base method appends the color value to .option-container__value on every
        // change (duplicating it); the label is owned by refreshOptionStates here.
        updateMobileColorLabel() {}

        /*
         * Called before the base handler resolves the variant. If the just-picked
         * combination has no live variant, keep the changed option and snap the
         * other option(s) to a live variant carrying the changed value.
         */
        reconcileSelection(event) {
          const changed = event.target;
          if (!changed || changed.type !== 'radio') return;

          const fieldsets = Array.from(this.querySelectorAll('fieldset'));
          const selected = this.getOptionsFromSelects();
          const live = this.liveVariants();

          const exists = live.some((variant) =>
            variant.options.every((value, index) => value === selected[index])
          );
          if (exists) return;

          const changedIndex = fieldsets.findIndex((fieldset) => fieldset.contains(changed));
          if (changedIndex === -1) return;

          const pool = live.filter((variant) => variant.options[changedIndex] === changed.value);
          const target = pool.find((variant) => variant.available) || pool[0];
          if (!target) return;

          fieldsets.forEach((fieldset, index) => {
            if (index === changedIndex) return;
            const radio = Array.from(fieldset.querySelectorAll('input[type="radio"]')).find(
              (input) => input.value === target.options[index]
            );
            if (radio) radio.checked = true;
          });
        }

        /*
         * Recompute .is-soldout / .is-unavailable on every option value against
         * the current selection. Mirrors the Liquid-side initial state logic in
         * the variant_picker block — keep the two in sync.
         */
        refreshOptionStates() {
          const fieldsets = Array.from(this.querySelectorAll('fieldset'));
          const selected = this.getOptionsFromSelects();
          const live = this.liveVariants();

          fieldsets.forEach((fieldset, index) => {
            // Option order varies by product (Wide Mouth is Size/Color, others
            // Color/Size) — key the size behavior on the option name, not index
            const isSize =
              fieldset.querySelector('.form__radio-btns-container')?.dataset.option === 'size';

            fieldset.querySelectorAll('input[type="radio"]').forEach((radio) => {
              const container = radio.closest('.form__radio-btn-container');
              if (!container) return;

              const withValue = live.filter(
                (variant) => variant.options[index] === radio.value
              );
              const matchingOthers = withValue.filter((variant) =>
                variant.options.every(
                  (value, optionIndex) => optionIndex === index || value === selected[optionIndex]
                )
              );

              let unavailable = false;
              let soldout = false;
              let disable = false;

              if (withValue.length === 0) {
                // No live variant has this value at all: hidden via CSS
                // (.is-unavailable with a disabled input)
                unavailable = true;
                disable = true;
              } else if (matchingOthers.length > 0) {
                soldout = !matchingOthers.some((variant) => variant.available);
              } else if (isSize) {
                // Size that doesn't exist in the selected color: struck-through
                // via .is-unavailable but NOT disabled — clicking keeps the size
                // and reconcileSelection snaps the color to the first available
                unavailable = true;
              } else if (index === 0) {
                // Non-size first option: selectable and full-strength even
                // though the current selection doesn't come in it —
                // reconcileSelection snaps the other option(s) on click
                soldout = !withValue.some((variant) => variant.available);
              } else {
                // Colors (or other options) that don't exist for the selected
                // size: hidden via CSS and dead, as before
                unavailable = true;
                disable = true;
              }

              const isSoldout = !unavailable && soldout;
              container.classList.toggle('is-unavailable', unavailable);
              container.classList.toggle('is-soldout', isSoldout);
              radio.toggleAttribute('disabled', disable);

              // Mirrors the initial state rendered in the variant_picker Liquid
              if (this.dataset.textSoldOut) {
                radio.setAttribute(
                  'aria-label',
                  isSoldout ? `${radio.value} — ${this.dataset.textSoldOut}` : radio.value
                );
              }
            });
          });

          this.updateSelectedValueLabels(selected, fieldsets);

          // Engrave row follows the selected variant's availability (Liquid
          // only renders the initial state; nothing else re-enables it)
          const engrave = document.getElementById('init-customizer');
          if (engrave) {
            const current = live.find((variant) =>
              variant.options.every((value, index) => value === selected[index])
            );
            engrave.toggleAttribute('disabled', !current || !current.available);
          }
        }

        updateSelectedValueLabels(selected, fieldsets) {
          this.querySelectorAll('.new-pdp-selected-value').forEach((label) => {
            const fieldset = label
              .closest('.fieldset-container')
              ?.querySelector('fieldset');
            const index = fieldsets.indexOf(fieldset);
            if (index !== -1) label.textContent = selected[index];
          });
        }
      }
    );
  }

  register();
  document.addEventListener('DOMContentLoaded', register);
})();

/*
 * Value props row below the ATC button: clicking an item reveals its
 * dismissable blurb below the row (one at a time, X to close).
 */
if (!customElements.get('new-pdp-value-props')) {
  customElements.define(
    'new-pdp-value-props',
    class NewPdpValueProps extends HTMLElement {
      connectedCallback() {
        this.items = Array.from(this.querySelectorAll('.new-pdp-value-props__item'));
        this.blurbs = Array.from(this.querySelectorAll('.new-pdp-value-props__blurb'));

        this.items.forEach((item) => {
          item.addEventListener('click', () => {
            const isOpen = item.getAttribute('aria-expanded') === 'true';
            this.open(isOpen ? null : item.dataset.valueProp);
          });
        });

        this.querySelectorAll('.new-pdp-value-props__close').forEach((button) => {
          button.addEventListener('click', () => {
            // The X is inside the blurb it hides, so focus would fall to <body>
            const blurb = button.closest('.new-pdp-value-props__blurb');
            const opener = blurb
              ? this.items.find((item) => item.dataset.valueProp === blurb.dataset.valueProp)
              : null;
            this.open(null);
            if (opener) opener.focus();
          });
        });
      }

      open(key) {
        this.blurbs.forEach((blurb) => {
          blurb.hidden = blurb.dataset.valueProp !== key;
        });
        this.items.forEach((item) => {
          const expanded = item.dataset.valueProp === key;
          item.setAttribute('aria-expanded', expanded ? 'true' : 'false');

          // The link label flips to "close" while the item's blurb is open
          const more = item.querySelector('.new-pdp-value-props__more');
          if (more) {
            if (!more.dataset.defaultText) more.dataset.defaultText = more.textContent.trim();
            const closeText = this.dataset.textClose || 'close';
            more.textContent = expanded ? closeText : more.dataset.defaultText;
          }
        });
      }
    }
  );
}